/**
 * SINGLE SOURCE OF TRUTH for a job's financial breakdown.
 *
 * Every surface that shows money for a job - the Job Detail profit card, the
 * dashboard, the kanban, the completed-jobs financial-year breakdown - must
 * call computeJobFinancials() rather than recompute these figures locally.
 *
 * This module deliberately returns every intermediate value, not just the
 * totals, so that no caller ever has a reason to redo part of the sum itself.
 *
 * Note on currency: this module returns raw numbers only. Formatting is the
 * caller's job, via formatCurrency(amount, region) from ./regionConfig, so
 * that the user's region determines the symbol and separators.
 */

import { getCrewSuperRate } from './regionConfig';

const sum = (rows, fn) => (rows || []).reduce((acc, row) => acc + (fn(row) || 0), 0);

const EMPTY = {
  jobValue: 0,
  generalExpenses: 0,
  hiddenCostsTotal: 0,
  crewLabourBaseCost: 0,
  crewEquipmentBaseCost: 0,
  crewSuperCost: 0,
  totalCrewBaseCosts: 0,
  crewLabourClientRate: 0,
  crewEquipmentClientRate: 0,
  crewLabourMargin: 0,
  crewEquipmentMargin: 0,
  totalCrewMargin: 0,
  profit: 0,
  profitMargin: 0,
  gst: 0,
};

/**
 * Compute the full financial breakdown for a single job.
 *
 * @param {object} job - a job document as returned by the API
 * @returns {object} every figure the UI needs, all raw numbers
 */
export function computeJobFinancials(job) {
  if (!job) return { ...EMPTY };

  // --- What we actually pay the crew (base rates, not what the client is charged)
  const crewLabourBaseCost = sum(
    job.crew_labour,
    (c) => (Number(c.base_rate) || 0) * (Number(c.days) || 0)
  );

  // Items billed directly by the supplier to the client never touch our books.
  const crewEquipmentBaseCost = sum(job.crew_equipment, (e) =>
    e.billed_direct
      ? 0
      : (Number(e.base_rate) || 0) * (Number(e.quantity) || 1) * (Number(e.days) || 0)
  );

  const crewSuperCost = Number(job.crew_superannuation?.total_amount) || 0;
  const totalCrewBaseCosts = crewLabourBaseCost + crewEquipmentBaseCost + crewSuperCost;

  // --- What the client is charged for that same crew
  const crewLabourClientRate = sum(
    job.crew_labour,
    (c) => (Number(c.client_rate) || 0) * (Number(c.days) || 0)
  );

  const crewEquipmentClientRate = sum(job.crew_equipment, (e) =>
    e.billed_direct
      ? 0
      : (Number(e.client_rate) || 0) * (Number(e.quantity) || 1) * (Number(e.days) || 0)
  );

  // --- Markup we keep on crew
  const crewLabourMargin = crewLabourClientRate - crewLabourBaseCost;
  const crewEquipmentMargin = crewEquipmentClientRate - crewEquipmentBaseCost;
  const totalCrewMargin = crewLabourMargin + crewEquipmentMargin;

  // --- Our own costs
  const generalExpenses = sum(
    job.expenses,
    (e) => (Number(e.amount) || 0) * (Number(e.quantity) || 1)
  );
  const hiddenCostsTotal = sum(
    job.hidden_costs,
    (c) => (Number(c.amount) || 0) * (Number(c.quantity) || 1)
  );

  // --- Job value excluding tax, as calculated and persisted by the backend
  const jobValue = Number(job.totals?.subtotal) || 0;
  const gst = Number(job.totals?.gst) || 0;

  // Net profit subtracts crew BASE costs, not client rates - the markup is ours.
  const profit = jobValue - generalExpenses - hiddenCostsTotal - totalCrewBaseCosts;
  const profitMargin = jobValue > 0 ? (profit / jobValue) * 100 : 0;

  return {
    jobValue,
    generalExpenses,
    hiddenCostsTotal,
    crewLabourBaseCost,
    crewEquipmentBaseCost,
    crewSuperCost,
    totalCrewBaseCosts,
    crewLabourClientRate,
    crewEquipmentClientRate,
    crewLabourMargin,
    crewEquipmentMargin,
    totalCrewMargin,
    profit,
    profitMargin,
    gst,
  };
}

/**
 * Best-available completion date for a job.
 * Prefers completed_at, falls back to updated_at, then created_at.
 */
export function getCompletionDate(job) {
  return job?.completed_at || job?.updated_at || job?.created_at || null;
}

/**
 * The day a job was completed, as YYYY-MM-DD in the viewer's time zone, for
 * a date picker. completed_at is either a UTC timestamp (stamped when the job
 * was marked completed) or local noon (set by hand); both read as the right day.
 */
export function completionDay(completedAt) {
  if (!completedAt) return '';
  const d = new Date(completedAt);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Effective per-unit rate for an equipment line item.
 *
 * Respects rate_mode, and - importantly - treats a rate_override of 0 as a
 * real override rather than "not set". A comped item is priced at zero, not
 * silently charged at its daily rate. `||` gets this wrong; only an explicit
 * null/undefined check is correct.
 */
export function getEquipmentItemRate(item) {
  if (!item) return 0;
  const override = item.rate_override;
  if (override !== null && override !== undefined) return Number(override) || 0;
  const base = item.rate_mode === 'week' ? item.weekly_rate : item.daily_rate;
  return Number(base) || 0;
}

/**
 * Line total for an equipment item: rate x duration x quantity.
 */
export function getEquipmentItemTotal(item) {
  if (!item) return 0;
  const days = Number(item.scheduled_days) || 0;
  const quantity = Number(item.quantity) || 0;
  return getEquipmentItemRate(item) * days * quantity;
}

/**
 * Subtotal, discount and total for one equipment package.
 *
 * The three discount types:
 *   percentage - discount is a percentage of the package subtotal
 *   budget     - the package total IS the budget, capped at the subtotal, so
 *                a budget above the subtotal is not an upsell
 *   fixed      - a flat discount, capped at the subtotal
 *
 * The budget cap matters: this used to be implemented three times, and the
 * Job Detail copy set total = discount_value uncapped, so a package whose
 * budget exceeded its items showed a higher total on screen than the backend
 * put on the invoice. These semantics match calculate_job_totals in
 * backend/job_service.py, which is what actually bills the client.
 *
 * @param {object} pkg - an equipment package: { equipment_ids, discount_type, discount_value }
 * @param {Array}  equipment - the job's equipment line items
 * @returns {{subtotal: number, discount: number, total: number, itemsInPackage: Array}}
 */
export function calculatePackageTotal(pkg, equipment) {
  const ids = pkg?.equipment_ids || [];
  const itemsInPackage = (equipment || []).filter(item => ids.includes(item.inventory_id));
  const subtotal = itemsInPackage.reduce((acc, item) => acc + getEquipmentItemTotal(item), 0);

  const value = Number(pkg?.discount_value) || 0;
  let discount;
  let total;

  if (pkg?.discount_type === 'percentage') {
    discount = subtotal * (value / 100);
    total = subtotal - discount;
  } else if (pkg?.discount_type === 'budget') {
    total = Math.min(value, subtotal);
    discount = subtotal - total;
  } else {
    discount = Math.min(value, subtotal);
    total = subtotal - discount;
  }

  return { subtotal, discount, total, itemsInPackage };
}

/**
 * What each equipment line credits its item when the job is completed: the
 * line total, less its share of any package discount, shared in proportion to
 * what each item put into the package. Returns one entry per line, in order:
 * { item, lineTotal, income }.
 *
 * Mirrors update_equipment_roi_for_job in backend/job_service.py, which writes
 * the income onto the item. The homepage uses this to show a job's lines
 * reaching the gear that earned them.
 */
export function equipmentIncomeByLine(job) {
  const equipment = job?.equipment || [];
  const discounted = {};
  (job?.equipment_packages || []).forEach((pkg) => {
    const { subtotal, discount, itemsInPackage } = calculatePackageTotal(pkg, equipment);
    if (subtotal <= 0) return;
    itemsInPackage.forEach((item) => {
      const total = getEquipmentItemTotal(item);
      discounted[item.inventory_id] = total - discount * (total / subtotal);
    });
  });
  return equipment.map((item) => {
    const lineTotal = getEquipmentItemTotal(item);
    const income = item.inventory_id in discounted ? discounted[item.inventory_id] : lineTotal;
    return { item, lineTotal, income };
  });
}

/**
 * What the client pays for a crew line, per day or per unit: the base rate plus
 * the markup percentage. The markup is our margin, so profit subtracts the base
 * rate, never this. Mirrors crew_client_rate in backend/item_rates.py; the cases
 * are asserted on both sides with the same numbers.
 */
export function crewClientRate(baseRate, markupPercent) {
  const base = Number(baseRate) || 0;
  return base + (base * (Number(markupPercent) || 0)) / 100;
}

/**
 * The crew_superannuation a job form should hold in this region.
 *
 * Crew super is Australian: in any other region it is null, so neither the
 * form nor the preview totals carry it - the backend clears it on save too
 * (calculate_job_totals). In Australia a job keeps the percentage it has, and
 * a new one starts at the region's default rate.
 *
 * @param {string} region - the user's region code
 * @param {object|null} existing - the job's stored crew_superannuation, if any
 */
export function crewSuperannuationFor(region, existing = null) {
  const rate = getCrewSuperRate(region);
  if (rate === null) return null;
  return existing || { percentage: rate, total_amount: 0 };
}

/**
 * A job's own equipment, before and after package discounts: every line at its
 * rate (an override of 0 is a comped line), then what the packages take off.
 * Works on a saved job or on the job form's state - both carry `equipment`
 * and `equipment_packages`. Crew equipment is not included; it is billed
 * separately.
 */
export function computeEquipmentTotals(job) {
  const equipmentSubtotal = sum(job?.equipment, getEquipmentItemTotal);
  const packageDiscountTotal = sum(job?.equipment_packages, (pkg) =>
    calculatePackageTotal(pkg, job?.equipment).discount);
  return { equipmentSubtotal, packageDiscountTotal, equipmentTotal: equipmentSubtotal - packageDiscountTotal };
}

/**
 * Live totals for the job form, as the user edits it.
 *
 * computeJobFinancials() above answers "what did this saved job earn?".
 * This answers "what does the estimate say right now?" while the form is still
 * being edited and nothing has been persisted. Both live here so that the two
 * questions cannot drift apart, and so neither page recomputes part of the sum
 * locally - JobBuilderPage and JobEditPage carried this block verbatim.
 *
 * The authoritative totals on a saved job are still the backend's
 * (job.totals, from calculate_job_totals). This is the pre-save preview.
 *
 * @param {object} formData - the job form's state
 * @returns {object} every figure the estimate summary shows, all raw numbers
 */
export function computeJobFormTotals(formData) {
  const f = formData || {};
  const rentalOnly = !!f.equipment_rental_only;

  // Crew and labour are excluded entirely on an equipment-only rental.
  const labourTotal = rentalOnly ? 0 : sum(f.phases, (phase) =>
    phase.day_rate
      ? phase.day_rate * phase.estimated_days
      : (phase.hourly_rate || 0) * phase.estimated_hours);

  const crewLabourTotal = rentalOnly ? 0 : sum(f.crew_labour, (crew) =>
    crew.client_rate * crew.days);

  // Self-billed crew equipment is invoiced by the crew member, not by us.
  const crewEquipmentTotal = rentalOnly ? 0 : sum(f.crew_equipment, (eq) =>
    eq.billed_direct ? 0 : eq.client_rate * eq.quantity * eq.days);

  // Super is a percentage of the marked-up crew labour, not of the base cost.
  const crewSuperTotal = crewLabourTotal > 0 && f.crew_superannuation
    ? crewLabourTotal * (f.crew_superannuation.percentage / 100)
    : 0;

  const { equipmentSubtotal, packageDiscountTotal } = computeEquipmentTotals(f);

  const expensesTotal = sum(f.expenses, (expense) =>
    (expense.amount || 0) * (expense.quantity || 1));

  const totalLabour = labourTotal + crewLabourTotal;
  const equipmentTotal = equipmentSubtotal - packageDiscountTotal;
  const totalEquipment = equipmentTotal + crewEquipmentTotal;
  const totalExpenses = expensesTotal + crewSuperTotal;

  const subtotal = totalLabour + totalEquipment + totalExpenses;
  const gstAmount = f.include_gst ? subtotal * ((f.gst_rate || 0) / 100) : 0;
  const grandTotal = subtotal + gstAmount;

  return {
    labourTotal,
    crewLabourTotal,
    crewEquipmentTotal,
    crewSuperTotal,
    totalLabour,
    equipmentSubtotal,
    packageDiscountTotal,
    equipmentTotal,
    totalEquipment,
    expensesTotal,
    totalExpenses,
    subtotal,
    gstAmount,
    grandTotal,
  };
}
